import { useState, useEffect, useCallback, useRef } from 'react';
import { CONNECTION_STATES, API_CONFIG } from '@shared/utils/constants';
import { getConnectionQuality, retryWithBackoff } from '@shared/utils/helpers';
import { useInterview } from '@features/interview/context/InterviewContext';

export const useNetwork = () => {
  const { actions } = useInterview();
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [connectionQuality, setConnectionQuality] = useState('unknown');
  const [latency, setLatency] = useState(null);
  const [lastChecked, setLastChecked] = useState(null);
  const pingIntervalRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const hasInitializedRef = useRef(false);

  // Check connection quality
  const checkConnectionQuality = useCallback(() => {
    const quality = getConnectionQuality();
    setConnectionQuality(quality);
    actions.setNetworkQuality(quality);
    return quality;
  }, [actions]);

  // Measure latency with a ping
  const measureLatency = useCallback(async () => {
    const startTime = performance.now();
    try {
      await fetch(`${API_CONFIG.BASE_URL}/ping`, {
        method: 'HEAD',
        cache: 'no-cache',
      });
      const endTime = performance.now();
      const measuredLatency = Math.round(endTime - startTime);
      setLatency(measuredLatency);
      setLastChecked(Date.now());
      return measuredLatency;
    } catch (error) {
      // If ping endpoint doesn't exist, try a simple online check
      return null;
    }
  }, []);

  // Handle online event
  const handleOnline = useCallback(() => {
    setIsOnline(true);
    actions.setConnectionState(CONNECTION_STATES.CONNECTED);
    actions.clearError();
    checkConnectionQuality();
    
    // Clear any reconnect timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  }, [actions, checkConnectionQuality]);

  // Handle offline event
  const handleOffline = useCallback(() => {
    setIsOnline(false);
    actions.setConnectionState(CONNECTION_STATES.DISCONNECTED);
    actions.setError({
      type: 'network',
      key: 'NETWORK_ERROR',
      message: 'انقطع الاتصال بالإنترنت. يرجى التحقق من اتصالك',
    });
    
    // Set reconnect timeout
    reconnectTimeoutRef.current = setTimeout(() => {
      if (!navigator.onLine) {
        actions.setError({
          type: 'network',
          key: 'TIMEOUT_ERROR',
          message: 'انتهت مهلة الاتصال. يرجى التحقق من اتصالك بالإنترنت',
        });
      }
    }, API_CONFIG.TIMEOUT);
  }, [actions]);

  // Start periodic connection monitoring
  const startMonitoring = useCallback((interval = 10000) => {
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
    }
    
    // Initial check
    checkConnectionQuality();
    
    // Periodic checks
    pingIntervalRef.current = setInterval(() => {
      checkConnectionQuality();
      measureLatency();
    }, interval);
  }, [checkConnectionQuality, measureLatency]);

  // Stop monitoring
  const stopMonitoring = useCallback(() => {
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
  }, []);

  // Attempt reconnection
  const attemptReconnect = useCallback(async () => {
    actions.setConnectionState(CONNECTION_STATES.RECONNECTING);
    
    try {
      await retryWithBackoff(async () => {
        if (!navigator.onLine) {
          throw new Error('لا يزال غير متصل');
        }
        await measureLatency();
      }, API_CONFIG.RETRY_ATTEMPTS, API_CONFIG.RETRY_DELAY);
      
      handleOnline();
      return true;
    } catch (error) {
      actions.setConnectionState(CONNECTION_STATES.ERROR);
      return false;
    }
  }, [actions, handleOnline, measureLatency]);

  // Setup event listeners
  useEffect(() => {
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Listen for connection changes
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (connection) {
      connection.addEventListener('change', checkConnectionQuality);
    }

    // Initial check (only once on mount)
    if (!hasInitializedRef.current && navigator.onLine) {
      checkConnectionQuality();
      hasInitializedRef.current = true;
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if (connection) {
        connection.removeEventListener('change', checkConnectionQuality);
      }
      stopMonitoring();
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [handleOnline, handleOffline, checkConnectionQuality, stopMonitoring]);

  return {
    // State
    isOnline,
    connectionQuality,
    quality: connectionQuality, // alias for easier use
    latency,
    lastChecked,
    
    // Actions
    checkConnectionQuality,
    measureLatency,
    startMonitoring,
    stopMonitoring,
    attemptReconnect,
  };
};

export default useNetwork;
